import { Mail, Phone } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { api } from "@/lib/api";
import WhatsAppTemplatePicker from "@/components/WhatsAppTemplatePicker";

function minutesSince(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 60000));
}

function freshnessTone(mins) {
  if (mins === null) return "fresh"; // never contacted
  if (mins < 60) return "cool"; // contacted recently
  if (mins < 240) return "warm"; // 1-4 hrs
  return "stale"; // 4hr+
}

function tonedotClass(tone) {
  // Visible coloured dot indicating last-contacted recency
  return {
    fresh: "bg-emerald-500",
    cool: "bg-rose-500",
    warm: "bg-amber-400",
    stale: "bg-slate-300",
  }[tone];
}

function timeAgo(mins) {
  if (mins === null) return "Never contacted";
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs > 1 ? "s" : ""} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
}

function buildTelUrl(phone) {
  // Strip ALL non-digits (including any leading '+') — pure digits only.
  // Some PBX / call-center dialers (MyOperator click-to-call, certain Android
  // dialers) misparse '+' and produce a broken dial string.
  const digits = String(phone || "").replace(/\D/g, "");
  return `tel:${digits}`;
}

function buildMailtoUrl(email) {
  return `mailto:${encodeURIComponent(email || "")}`;
}

/**
 * Inline contact actions for a lead row.
 * Logs an attempt to the backend BEFORE opening the deep-link.
 * Props:
 *  - lead: lead doc (must have id, phone, email, attempts)
 *  - disabled: bool — closed/paused leads dim the icons
 *  - onAttemptLogged: (channel) => void   // tells parent to refetch / patch lead
 *  - onAfterCall: () => void              // parent opens DispositionModal
 */
export default function LeadContactActions({
  lead,
  disabled = false,
  onAttemptLogged,
  onAfterCall,
}) {
  if (!lead) return null;

  const lastCall = lead.attempts?.last_at?.call;

  const callMins = minutesSince(lastCall);

  const logAttempt = async (channel) => {
    try {
      await api.post(`/admin/leads/${lead.id}/attempts`, {
        channel,
        source: "inline_table",
      });
      onAttemptLogged?.(channel);
    } catch {
      /* non-blocking — we still let the deep-link open */
    }
  };

  const onCall = (e) => {
    e.stopPropagation();
    if (disabled) return;
    logAttempt("call");
    // Open native dialler. After we expect counsellor to return → fire modal.
    window.location.href = buildTelUrl(lead.phone);
    // Trigger disposition modal flow (parent decides when to show)
    onAfterCall?.();
  };

  const onEmail = (e) => {
    e.stopPropagation();
    if (disabled || !lead.email) return;
    // Email is low-emphasis — not part of cadence; we don't log an attempt here.
    window.location.href = buildMailtoUrl(lead.email);
  };

  const baseBtn =
    "relative inline-flex h-11 w-11 items-center justify-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0A2540] disabled:opacity-40 disabled:cursor-not-allowed";

  return (
    <TooltipProvider delayDuration={120}>
      <div
        className="flex items-center gap-1 sm:gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        {/* WhatsApp — template picker + reply prompt */}
        <WhatsAppTemplatePicker
          lead={lead}
          disabled={disabled}
          baseBtn={baseBtn}
          onLogAttempt={logAttempt}
          onChanged={() => onAttemptLogged?.("whatsapp")}
        />

        {/* Call */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onCall}
              disabled={disabled}
              aria-label={`Call ${lead.name}`}
              className={`${baseBtn} text-[#0A2540] hover:bg-[#0A2540]/10`}
              data-testid={`contact-call-${lead.id}`}
            >
              <Phone className="h-4 w-4" strokeWidth={2.2} />
              <Dot tone={freshnessTone(callMins)} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[220px] text-xs">
            <div className="font-semibold">Call</div>
            <div className="text-slate-300">{lead.phone}</div>
            <div className="mt-1 text-slate-400">{timeAgo(callMins)}</div>
          </TooltipContent>
        </Tooltip>

        {/* Email (low-emphasis) */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onEmail}
              disabled={disabled || !lead.email}
              aria-label={`Email ${lead.name}`}
              className={`${baseBtn} text-slate-500 hover:bg-slate-200`}
              data-testid={`contact-email-${lead.id}`}
            >
              <Mail className="h-4 w-4" strokeWidth={2} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[220px] text-xs">
            <div className="font-semibold">Email</div>
            <div className="text-slate-300">{lead.email || "no email"}</div>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}

function Dot({ tone }) {
  const cls = tonedotClass(tone);
  if (!cls) return null;
  return (
    <span
      className={`absolute right-1 top-1 h-1.5 w-1.5 rounded-full ${cls}`}
      aria-hidden
    />
  );
}
